import React, { useEffect, useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import config from '@/config'
import moment from 'moment'
import { makeStyles, useTheme } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import request from '@/modules/base/util/request'
import settingsConfig from '@/modules/settings/config'
import { ModalBig } from '@/modules/base/components/modal'
import { SelectInput } from '@/modules/base/components/inputs'
import IconCheck from '@assets/images/icons/check.svg'
import lang from '@/lang'
import { ButtonIconSquare, CancelButton } from '@/modules/base/components/buttons'
import { fetchSubscriptionData } from '@/redux/ducks/subscription/details'
import { fetchData as fetchUserData } from '@/redux/ducks/user/info'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconSuccess from '@assets/images/successful.svg'
import IconFailed from '@assets/images/failed.svg'
import IconArrowRight from '@assets/images/icons/arrowRight.svg'
import webstorageService from '@/modules/_services/webstorage.service'
import webStorageKeyEnum from '@/enums/web-storage-key.enum'

const useStyles = makeStyles((theme) => ({
  contentWrapper: {
    minHeight: 400,
  },
  content: {
    width: '100%',
    paddingBottom: 10,
    paddingTop: 10,
    display: 'flex',
    gap: 20,
    color: theme.black,
  },
  wrapper: {
    display: 'flex',
    fontSize: theme.fontSizes.smaller,
    alignItems: 'center',
    paddingBottom: 10,
  },
  error: {
    color: theme.red,
    paddingLeft: 10,
  },
  success: {
    color: theme.green,
    paddingLeft: 10,
  },
  actions: {
    padding: '20px 32px',
    borderTop: theme.border,
    backgroundColor: theme.backgroundColor,
    '& > button:not(:first-child)': {
      marginLeft: 20,
    },
  },
  title: {
    color: theme.black,
    marginBottom: 14,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
  },
  arrowIcon: {
    marginTop: 30,
  },
  iconWrapper: {
    margin: 'auto',
  },
  danglingSubscription: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: theme.fontSizes.small,
    marginBottom: 10,
  },
  subscription: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: theme.border,
    borderRadius: theme.borderRadius,
    padding: '2px 8px',
  },
  subscriptionDate: {
    fontSize: theme.fontSizes.smaller,
    color: theme.labelLight,
  },
}))

const SubscriptionDanglingModal = ({ open, onClose }) => {
  const classes = useStyles()
  const theme = useTheme()

  const dispatch = useDispatch()

  const { tenants, activeTenant } = useSelector(state => state.user.info)
  const allowedTenants = tenants.filter(t => t.isOwner && !t.isDemo)
  allowedTenants.unshift({ tenantId: 'later', tenantName: lang.myAccount.danglingSubscription.assignLater })

  const [danglingSubscriptions, setDanglingSubscriptions] = useState([])

  const [selectedTenant, setSelectedTenant] = useState(danglingSubscriptions.map(ds => allowedTenants?.[0]?.tenantId))

  const onTenantChange = (tenantId, index) => {
    const newSelectedTenant = [...selectedTenant]
    newSelectedTenant[index] = tenantId
    setSelectedTenant(newSelectedTenant)
  }

  useEffect(() => {
    request(settingsConfig.getOrSetDanglingSubscriptions, { auth: true })
      .then((response) => {
        const { body: { data } } = response
        setDanglingSubscriptions(data)
        setSelectedTenant(data.map(() => allowedTenants?.[0]?.tenantId))
      }).catch((error) => {
        console.error(error)
      })
  }, [])

  const onSubmit = () => {
    const assignments = danglingSubscriptions.map((ds, index) => {
      return {
        subscriptionId: ds.subscription_id,
        tenantId: selectedTenant[index],
      }
    })
    return request(settingsConfig.getOrSetDanglingSubscriptions, { auth: true, method: 'POST', data: { assignments } })
      .then((response) => {
        const { body: { data: { success, errors } } } = response
        const newDanglingSubs = danglingSubscriptions.map(ds => {
          delete ds.success
          delete ds.message
          delete ds.error
          return ds
        })
        success.forEach((item) => {
          const index = newDanglingSubs.findIndex((ds) => ds.subscription_id === item.subscriptionId)
          newDanglingSubs[index].success = true
          newDanglingSubs[index].message = lang.myAccount.danglingSubscription.assignSuccess
        })
        errors.forEach((item) => {
          const index = newDanglingSubs.findIndex((ds) => ds.subscription_id === item.subscriptionId)
          newDanglingSubs[index].error = item.error
          newDanglingSubs[index].message = lang.myAccount.danglingSubscription.assignError?.[Object.keys(item.code)[0]]?.[Object.values(item.code)[0]] || lang.defaultErrorMessage
        })
        if (success.length) {
          // remove readonly if current tenant gets a subscription assigned
          const activeTenantSub = success.find(({ tenantId }) => tenantId === activeTenant?.tenantId)
          if (activeTenantSub) {
            webstorageService.removeItem(webStorageKeyEnum.READ_ONLY_CONFIRMED)
          }
          dispatch(fetchUserData())
          fetchSubscriptionData(true).then(() => {
            if (!errors.length) {
              onClose()
            }
          })
        }
        setDanglingSubscriptions(newDanglingSubs)
      }).catch((error) => {
        console.error(error)
      })
  }

  const getTenantLabel = ({ tenantName, companyName }) => {
    if (tenantName && companyName) {
      return `${tenantName} - ${companyName}`
    } else if (companyName) {
      return companyName
    } else {
      return tenantName || lang.tenant.newTenant
    }
  }

  const mappedTenants = allowedTenants.map(tenant => ({
    value: tenant.tenantId,
    label: getTenantLabel(tenant),
  }))

  const actions = [
    <CancelButton
      label={lang.myAccount.danglingSubscription.assignLater}
      onClick={onClose}
      key='cancel-dangling-subscription-modal'
    />,
    <ButtonIconSquare
      label={lang.generics.assign}
      icon={IconCheck}
      onClick={onSubmit}
      key='submit-dangling-subscription-modal'
    />,
  ]

  return (
    <ModalBig
      title={lang.myAccount.danglingSubscription.modalTitle}
      titleClass={classes.title}
      subtitle={lang.myAccount.danglingSubscription.modalSubtitle}
      lineHeight={'1.5'}
      actions={actions}
      actionClass={classes.actions}
      open={open}
      width={800}
      maxWidth={'lg'}
      paddingtop={0}
      onClose={onClose}
    >
      <div className={classes.contentWrapper}>
        {danglingSubscriptions.map((ds, index) => {
          const allowedTenantIds = allowedTenants.filter(t => !(t.subscriptions || []).filter(s => !s.isTrial).map(s => s.type).includes(ds.typeOfSubscription)).map(t => t.tenantId)
          return (
            <Fragment key={`dangling-subscription-${index}`}>
              <div className={classes.content}>
                <div className={classes.danglingSubscription}>
                  <div className={classes.label}>
                    {lang.myAccount.danglingSubscription.subscription}
                  </div>
                  <div className={classes.subscription}>
                    <div>{lang.myAccount.getSubscriptionTitle(ds.title || ds.plan_id)}</div>
                    <div className={classes.subscriptionDate}>{`${lang.myAccount.danglingSubscription.startAt} ${moment(ds.subscription_current_term_start).format(config.dateFormat.client)}`}</div>
                  </div>
                </div>
                <div className={classes.iconWrapper}>
                  <SVGIcon svg={IconArrowRight} className={clsx(classes.icon, classes.arrowIcon)} width={'20px'} height={'20px'} fill={theme.black} />
                </div>
                <SelectInput
                  name='tenantSelection'
                  notAsync
                  allowCreate={false}
                  value={selectedTenant[index]}
                  label={lang.myAccount.danglingSubscription.tenant}
                  options={mappedTenants.filter(t => allowedTenantIds.includes(t.value))}
                  handleChange={(tenant) => onTenantChange(tenant.value, index)}
                />
              </div>
              {ds.message && <div className={classes.wrapper}>
                {ds.error && <SVGIcon svg={IconFailed} className={classes.icon} width={'20px'} height={'20px'} fill={theme.red} />}
                {ds.success && <SVGIcon svg={IconSuccess} className={classes.icon} width={'20px'} height={'20px'} fill={theme.green} />}
                {ds.message && <span className={ds.success ? classes.success : classes.error}>{ds.message}</span>}
              </div>}
            </Fragment>
          )
        })}
      </div>
    </ModalBig>
  )
}

SubscriptionDanglingModal.propTypes = {
  wrapperClass: PropTypes.string,
}

export default SubscriptionDanglingModal
